import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { Table, Tabs } from "antd";
import { toDateString, toSetString, toCaseDetailLink, toReexamDetailLink, toPatentDetailLink } from "../../helpers/converters";
import { isArray, startCase } from "lodash";

const toIndustryText = v =>
  v
    ? {
      T: "High-Tech",
      M: "Medical",
      O: "Other"
    }[v]
    : v;

const ptabCols = [
  {
    dataIndex: "case_number",
    title: "Case #",
    width: 150,
    render: toCaseDetailLink
  },
  {
    dataIndex: "filing_date",
    title: "Filing Date",
    width: 120,
    render: toDateString
  },
  {
    dataIndex: "up_status_concat",
    title: "Status",
    width: 120
  },
  {
    dataIndex: "up_industry",
    title: "Industry",
    width: 80,
    render: toIndustryText
  },
  {
    dataIndex: "patent_owners",
    title: "Patent Owner",
    width: 200,
    render: toSetString
  },
  {
    dataIndex: "petitioners",
    title: "Petitioner",
    width: 200,
    render: toSetString
  }
];
const litigationCols = [
  {
    title: "Case Number",
    dataIndex: "case_no",
    key: "case_no",
    width: 160,
    render: (caseNum, record) => <Link to={`/litigation/${record.court}/case/${caseNum}`}>{caseNum}</Link>
  },
  {
    title: "Filing Date",
    dataIndex: "filed_date",
    key: "filed_date",
    width: 140,
    render: toDateString
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 100
  },
  {
    title: "Court",
    dataIndex: "court",
    key: "court",
    width: 300
  },
  {
    title: "Cause of Action",
    dataIndex: "cause_of_action",
    key: "cause_of_action",
    width: 150,
    render: val => (isArray(val) ? toSetString(val) : val)
  },
  {
    title: "Industry",
    dataIndex: "industry",
    key: "industry",
    width: 100,
    render: toIndustryText
  },

  {
    title: "Defendant",
    dataIndex: "defendant",
    key: "defendant",
    width: 200,
    render: toSetString
  },
  {
    title: "Plaintiff",
    dataIndex: "plaintiff",
    key: "plaintiff",
    width: 200,
    render: toSetString
  }
];
const exparteCols = [
  {
    dataIndex: "application_number",
    title: "Application",
    width: 100,
    render: val => toReexamDetailLink(val)
  },
  {
    dataIndex: "filing_date",
    title: "Filing Date",
    width: 100,
    render: toDateString
  },
  {
    dataIndex: "exparte_status",
    title: "Status",
    width: 200,
  },
  {
    dataIndex: "owner",
    title: "Patent Owner",
    width: 200,
    render: toSetString
  },
  {
    dataIndex: "requester",
    title: "Requester",
    width: 200,
    render: toSetString
  }
];
const patentCols = [
  {
    title: "Patent Number",
    dataIndex: "publication_number",
    key: "publication_number",
    width: 180,
    render: toPatentDetailLink
  },
  {
    title: "Patent Title",
    dataIndex: "title",
    key: "title"
  },
  {
    title: "Publication Date",
    dataIndex: "publication_date",
    key: "publication_date",
    render: toDateString,
    width: 150
  },
  {
    title: "Priority Date",
    dataIndex: "priority_date",
    key: "priority_date",
    render: toDateString,
    width: 150
  },
  {
    title: "Assignee",
    dataIndex: "assignee_current",
    key: "assignee_current",
    width: 200,
    render: toSetString
  }
];

const tableProps = {
  ptab: {
    petitioners: {
      columns: ptabCols,
      rowKey: "case_number"
    },
    patent_owners: {
      columns: ptabCols,
      rowKey: "case_number"
    }
  },
  litigation: {
    plaintiff: {
      columns: litigationCols,
      rowKey: "case_id"
    },
    defendant: {
      columns: litigationCols,
      rowKey: "case_id"
    }
  },
  exparte: {
    requester: {
      columns: exparteCols,
      rowKey: "application_number"
    },
    owner: {
      columns: exparteCols,
      rowKey: "application_number"
    }
  },
  patent: {
    assignee_current: {
      columns: patentCols,
      rowKey: "publication_number"
    }
  }
};

const linkSearchProps = {
  ptab: {
    pathname: "/ptab/caselist",
    label: "View All Cases",
  },
  litigation: {
    pathname: "/litigation/caselist",
    label: "View All Cases",
  },
  exparte: {
    pathname: "/exparte/search",
    label: "View All Reexams",
  },
  patent: {
    pathname: "/patents/search",
    label: "View All Patents",
  }
}
const linkAnalyticsProps = {
  ptab: {
    pathname: "/ptab/analytics/case-level/by-status-and-phase",
    label: "View Analytics",
  },
  litigation: {
    pathname: "/litigation/analytics",
    label: "View Analytics",
  },
  exparte: {
    pathname: "/exparte/analytics",
    label: "View Analytics",
  },
  patent: {
    pathname: "/patents/analytics/top-assignees",
    label: "View Analytics",
  }
}

const RelatedCases = ({ source, counts, cases, name, loading }) => {
  const fields = Object.keys(tableProps[source]);
  const [field, setField] = useState(fields[0]);

  // preferably show the field with non-empty cases
  useEffect(() => {
    if (counts[field] === 0) {
      const nextField = fields.find(f => counts[f] > 0);
      if (nextField) setField(nextField);
    }
  }, [source, counts]);

  const footerRenderer = () => (
    <div style={{ display: "flex", justifyContent: "space-around" }}>
      <Link to={{ pathname: linkSearchProps[source].pathname, query: { [field]: name } }}>{linkSearchProps[source].label}</Link>
      <Link to={{ pathname: linkAnalyticsProps[source].pathname, query: { [field]: name } }}>{linkAnalyticsProps[source].label}</Link>
    </div>
  )
  return (
    <>
      <Tabs
        activeKey={field}
        onChange={setField}
        size="small"
        items={Object.keys(tableProps[source]).map(k => ({
          key: k,
          label: `${startCase(k)} (${loading ? "..." : counts[k].toLocaleString()})`
        }))}
      />
      <Table
        dataSource={cases[field]}
        {...tableProps[source][field]}
        loading={loading}
        size="small"
        pagination={false}
        footer={footerRenderer}
        scroll={{ x: 800, y: 400 }}
      />
    </>
  )
}
RelatedCases.propTypes = {
  source: PropTypes.oneOf(["ptab", "litigation", "exparte", "patent"]).isRequired,
  counts: PropTypes.object.isRequired,
  cases: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired
}

export default RelatedCases;